import { computed, ref } from 'vue'

import type { Ref, ComputedRef } from 'vue'
import type { MultiselectSearchOption } from '@shared/ui/fields'
import type { KanbanAgent } from '@entities/task'

import { useTutorKanbanStore } from '../store'

interface FilterAgentsOptions {
  initial: string[]
}

interface FilterAgentsReturn {
  options: ComputedRef<MultiselectSearchOption[]>
  query: Ref<string[]>
  initialValue: ComputedRef<string[]>
  handleSelected: (selected: MultiselectSearchOption[]) => void
}

export const useFilterAgents = ({ initial }: FilterAgentsOptions): FilterAgentsReturn => {
  const tutorKanbanStore = useTutorKanbanStore()

  const query = ref<string[]>(initial)

  const options = computed<MultiselectSearchOption[]>(() => {
    const agents = tutorKanbanStore.getAgents
    const names = agents.map((a: KanbanAgent) => a.name)

    const agentOptions = agents.map((agent: KanbanAgent) => {
      const id = String(agent.id)
      const isDuplicatedName = names.filter((n: string) => n === agent.name).length > 1
      const text = isDuplicatedName ? `${agent.name} (id: ${id})` : agent.name

      return {
        id,
        value: id,
        text,
        selected: query.value.includes(id),
      }
    })

    const unassigned: MultiselectSearchOption = {
      id: '0',
      value: '0',
      text: 'Unassigned agent',
      selected: query.value.includes('0'),
      color: 'red',
    }

    return [unassigned, ...agentOptions]
  })

  const initialValue = computed<string[]>(() => {
    return options.value
      .filter(option => option.selected)
      .map(option => option.text)
  })

  const handleSelected = (selected: MultiselectSearchOption[]): void => {
    query.value = selected.map(option => String(option.id))
  }

  return {
    options,
    query,
    initialValue,
    handleSelected,
  }
}
